import { toApiError, toTransportError, parseBody } from './error-taxonomy.js'
import { singleFlightRefresh, type RefreshSession } from './session-refresh.js'
import { ApiError } from './types.js'
import type { HttpClient, Interceptor } from './client.js'
import type { RequestOptions } from './types.js'

export interface FetchClientOptions {
  /** Prefixed to every path. A PATH, never an absolute URL — see `buildUrl`. */
  readonly baseUrl: string
  readonly defaultTimeoutMs?: number
  readonly interceptors?: readonly Interceptor[]
  /**
   * Exchange the refresh cookie for a new session when a request 401s.
   *
   * Injected rather than called directly, because this layer has no endpoint knowledge —
   * it knows five verbs and nothing about `/admin/auth/refresh`.
   */
  readonly refreshSession?: RefreshSession
}

const DEFAULT_TIMEOUT_MS = 15_000

/**
 * The only place in the SPA that calls `fetch`.
 *
 * Every failure leaves here as an `ApiError`, so a caller has exactly one thing to catch.
 */
export function createFetchClient(options: FetchClientOptions): HttpClient {
  const timeoutMs = options.defaultTimeoutMs ?? DEFAULT_TIMEOUT_MS
  const interceptors = options.interceptors ?? []
  // Wrapped once per client, not per request: the guard is only useful if every request
  // shares it. Six widgets 401ing together must produce ONE refresh, because the backend
  // rotates the token and the second exchange would spend one the first already used.
  const refresh =
    options.refreshSession === undefined ? undefined : singleFlightRefresh(options.refreshSession)

  /**
   * Whether this failure was a 401 that a successful refresh has now fixed.
   *
   * Returns `true` only when the session was actually renewed, so the caller's single
   * `if` means "retry it". Everything that disqualifies a retry — no refresher wired, a
   * non-auth failure, the refresh call itself, the retry that followed one — answers
   * `false` here rather than adding a branch to `request`.
   */
  async function renewed(error: unknown, requestOptions?: RequestOptions): Promise<boolean> {
    if (refresh === undefined) return false
    if (!(error instanceof ApiError) || !error.isUnauthenticated) return false
    if (requestOptions?.skipRefresh === true) return false

    return await refresh()
  }

  /** Every interceptor, in array order. Extracted to keep `request` within its caps. */
  async function notify(error: unknown, url: string): Promise<void> {
    for (const interceptor of interceptors) {
      await interceptor.onError?.(error, url)
    }
  }

  async function request<T>(
    method: string,
    path: string,
    body?: unknown,
    requestOptions?: RequestOptions,
  ): Promise<T> {
    const controller = new AbortController()
    let timedOut = false

    const timer = setTimeout(() => {
      timedOut = true
      controller.abort()
    }, requestOptions?.timeoutMs ?? timeoutMs)

    // The URL is built INSIDE the try: a throw from `buildUrl` above it would bypass the
    // error mapping, bypass every interceptor, and leak the timer with its controller.
    let url = path

    try {
      url = buildUrl(options.baseUrl, path, requestOptions?.query)

      const response = await fetch(url, {
        ...buildInit(method, body, requestOptions),
        signal: linkSignals(controller.signal, requestOptions?.signal),
      })

      if (!response.ok) throw await toApiError(response, url)

      return await parseBody<T>(response, url)
    } catch (error: unknown) {
      // An `ApiError` already came from `toApiError` — the server answered and we read
      // it. Anything else never reached a server, so it maps through the transport
      // taxonomy, which also rethrows a caller's own abort untouched.
      const final = error instanceof ApiError ? error : toTransportError(error, url, timedOut)

      // BEFORE the interceptors, deliberately. The 401 interceptor redirects to the login
      // screen, so a refresh that succeeds has to pre-empt it — otherwise the operator is
      // bounced out of a session that was just renewed.
      if (await renewed(final, requestOptions)) {
        // `skipRefresh` on the retry: one attempt, never a loop.
        return await request<T>(method, path, body, { ...requestOptions, skipRefresh: true })
      }

      await notify(final, url)

      throw final
    } finally {
      clearTimeout(timer)
    }
  }

  return {
    get: <T>(path: string, o?: RequestOptions) => request<T>('GET', path, undefined, o),
    post: <T>(path: string, b?: unknown, o?: RequestOptions) => request<T>('POST', path, b, o),
    put: <T>(path: string, b?: unknown, o?: RequestOptions) => request<T>('PUT', path, b, o),
    patch: <T>(path: string, b?: unknown, o?: RequestOptions) => request<T>('PATCH', path, b, o),
    delete: <T>(path: string, o?: RequestOptions) => request<T>('DELETE', path, undefined, o),
  }
}

/**
 * The request, minus the signal.
 *
 * Extracted so `request` stays under the complexity cap — the conditional spreads that
 * keep `Content-Type` and `body` off a GET are each a branch, and there are four of them.
 */
function buildInit(method: string, body: unknown, options?: RequestOptions): RequestInit {
  return {
    method,
    // `same-origin`, not `include`. The session cookie is SameSite=Strict on this origin
    // so `same-origin` sends it — and unlike `include` it cannot send it anywhere else,
    // which means a mistaken absolute `baseUrl` fails loudly instead of quietly posting
    // the session to another host.
    credentials: 'same-origin',
    headers: {
      Accept: 'application/json',
      ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
      ...options?.headers,
    },
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  }
}

/**
 * Joins the base and the path without letting either swallow the other.
 *
 * **Leading slashes are stripped from the path.** `'/api' + '/admin/users'` is the obvious
 * case, but `new URL('/admin/users', 'https://host/api/')` resolves to
 * `https://host/admin/users` — silently dropping the prefix and sending an authenticated
 * request somewhere nobody intended.
 */
export function buildUrl(
  base: string,
  path: string,
  query?: Readonly<Record<string, string | number | boolean | undefined>>,
): string {
  const prefix = base.endsWith('/') ? base.slice(0, -1) : base
  const suffix = path.replace(/^\/+/, '')
  const url = `${prefix}/${suffix}`

  if (query === undefined) return url

  const params = new URLSearchParams()

  for (const [key, value] of Object.entries(query)) {
    // An absent filter is absent from the URL, never `?status=undefined`. The backend
    // rejects unknown and malformed query fields, so an empty one is a 422.
    if (value !== undefined && value !== '') params.set(key, String(value))
  }

  const search = params.toString()

  return search === '' ? url : `${url}?${search}`
}

/** The caller's own abort, plus ours, without either cancelling the other's. */
function linkSignals(own: AbortSignal, caller?: AbortSignal): AbortSignal {
  return caller === undefined ? own : AbortSignal.any([own, caller])
}
